import React from 'react';
import { FilmSlateIcon, XIcon } from '@phosphor-icons/react';
import { ScriptBlockData } from './ScriptBlock';
import { Button } from '../ui/button';

interface SceneNavigatorProps {
    blocks: ScriptBlockData[];
    onSceneClick: (blockId: string) => void;
    onClose: () => void;
    activeBlockId?: string | null;
}

export const SceneNavigator: React.FC<SceneNavigatorProps> = ({
    blocks,
    onSceneClick,
    onClose,
    activeBlockId
}) => {
    // Extract scenes (scene headings) with their positions
    const scenes = blocks
        .map((block, index) => ({ block, index }))
        .filter(({ block }) => block.type === 'scene-heading')
        .map(({ block, index }, sceneIndex) => ({
            id: block.id,
            content: block.content || 'UNTITLED SCENE',
            index,
            sceneNumber: block.richText?.paragraphAttributes.Number || sceneIndex + 1
        }));

    // Find which scene the active block is in
    const getActiveScene = () => {
        if (!activeBlockId) return null;
        const activeIndex = blocks.findIndex(b => b.id === activeBlockId);
        if (activeIndex === -1) return null;

        // Find the most recent scene heading before this block
        for (let i = activeIndex; i >= 0; i--) {
            if (blocks[i].type === 'scene-heading') {
                return blocks[i].id;
            }
        }
        return null;
    };

    const activeSceneId = getActiveScene();

    return (
        <div className="w-64 h-full bg-zinc-900 border-l border-white/10 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <FilmSlateIcon className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-medium text-zinc-200">Scenes</span>
                    <span className="text-xs text-zinc-500">({scenes.length})</span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="h-6 w-6 p-0 text-zinc-500 hover:text-white"
                >
                    <XIcon className="w-3.5 h-3.5" />
                </Button>
            </div>

            {/* Scene List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {scenes.length === 0 ? (
                    <div className="px-3 py-8 text-center">
                        <FilmSlateIcon className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                        <p className="text-sm text-zinc-500">No scenes yet</p>
                        <p className="text-xs text-zinc-600 mt-1">Add a Scene Heading to start</p>
                    </div>
                ) : (
                    scenes.map((scene) => (
                        <button
                            key={scene.id}
                            onClick={() => onSceneClick(scene.id)}
                            className={`w-full flex items-start gap-2 p-2 rounded-lg text-left transition-all group ${
                                scene.id === activeSceneId
                                    ? 'bg-indigo-500/20 border border-indigo-500/30'
                                    : 'hover:bg-white/5 border border-transparent'
                            }`}
                        >
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                scene.id === activeSceneId
                                    ? 'bg-indigo-500/30 text-indigo-300'
                                    : 'bg-zinc-800 text-zinc-500 group-hover:text-zinc-400'
                            }`}>
                                {scene.sceneNumber}
                            </span>
                            <span className={`text-xs font-medium leading-relaxed ${
                                scene.id === activeSceneId
                                    ? 'text-indigo-200'
                                    : 'text-zinc-400 group-hover:text-zinc-200'
                            }`}>
                                {scene.content.length > 40
                                    ? scene.content.substring(0, 40) + '...'
                                    : scene.content
                                }
                            </span>
                        </button>
                    ))
                )}
            </div>

            {/* Footer Stats */}
            <div className="p-3 border-t border-white/5 bg-white/[0.02]">
                <div className="text-[10px] text-zinc-600 text-center">
                    Click a scene to jump to it
                </div>
            </div>
        </div>
    );
};
